import { Link } from "react-router-dom";
import HeroCarousel from "./components/HeroCarousel";
import SectionHeading from "./components/SectionHeading";
import WorkCard from "@/components/feature/WorkCard";
import Badge from "@/components/base/Badge";
import { news, reviews, works } from "@/utils/data";
import { useSeo } from "@/hooks/useSeo";

const quickEntries = [
  { title: "电影", desc: "最新上映与经典佳作", icon: "ri-film-line", to: "/category/movie", tone: "primary" as const },
  { title: "电视剧", desc: "热播剧集与更新动态", icon: "ri-tv-2-line", to: "/category/tv", tone: "accent" as const },
  { title: "影评专题", desc: "深度解析与观影笔记", icon: "ri-chat-quote-line", to: "/category/review", tone: "secondary" as const },
];

export default function Home() {
  useSeo({
    title: "亚洲天堂-最新电影电视剧资讯与影评分享平台",
    description:
      "亚洲天堂为您提供丰富的电影、电视剧资讯与影评内容，涵盖多类型影视作品，让影迷快速了解影视动态并分享观影体验。",
    keywords: "亚洲天堂,电影资讯,电视剧动态,影视影评,最新影视,影视分享,电影推荐",
  });

  const focusNews = news.slice(0, 4);
  const latestNews = news.slice(0, 6);
  const hotReviews = reviews.slice(0, 4);
  const hotWorks = [...works].sort((a, b) => b.rating - a.rating).slice(0, 8);

  const toneStyles: Record<string, string> = {
    primary: "bg-primary-100 text-primary-700",
    accent: "bg-accent-100 text-accent-700",
    secondary: "bg-secondary-100 text-secondary-700",
  };

  return (
    <div className="animate-fade-in">
      <HeroCarousel items={focusNews} />

      {/* 分类快捷入口 */}
      <section className="mx-auto w-full max-w-site px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {quickEntries.map((entry) => (
            <Link
              key={entry.title}
              to={entry.to}
              className="group flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-5 transition-all duration-300 hover:-translate-y-1 hover:border-primary-200"
            >
              <span className={`flex h-12 w-12 items-center justify-center rounded-lg text-2xl ${toneStyles[entry.tone]}`}>
                <i className={entry.icon} aria-hidden="true"></i>
              </span>
              <span className="flex-1">
                <span className="block font-heading text-base font-bold text-foreground-900">{entry.title}</span>
                <span className="block text-xs text-foreground-500">{entry.desc}</span>
              </span>
              <i className="ri-arrow-right-line text-xl text-foreground-300 transition-transform group-hover:translate-x-1 group-hover:text-accent-500" aria-hidden="true"></i>
            </Link>
          ))}
        </div>
      </section>

      {/* 最新资讯 + 热门影评 */}
      <section className="bg-background-100">
        <div className="mx-auto grid w-full max-w-site grid-cols-1 gap-10 px-4 py-12 md:px-6 md:py-16 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <SectionHeading eyebrow="News" title="最新影视资讯" moreTo="/category/review" moreLabel="更多资讯" />
            <div className="flex flex-col divide-y divide-background-200 overflow-hidden rounded-lg border border-background-200 bg-background-50">
              {latestNews.map((item) => (
                <Link
                  key={item.id}
                  to={`/detail/news/${item.id}`}
                  className="group flex gap-4 p-4 transition-colors hover:bg-background-100"
                >
                  <span className="relative h-20 w-28 shrink-0 overflow-hidden rounded-md bg-background-200">
                    <img
                      src={item.cover}
                      alt={item.title}
                      title={`${item.title} - 影视资讯 - 亚洲天堂`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </span>
                  <span className="flex flex-1 flex-col">
                    <span className="mb-1 flex items-center gap-2 text-xs text-foreground-400">
                      <i className="ri-time-line" aria-hidden="true"></i>
                      {item.date}
                      <span className="text-foreground-300">·</span>
                      {item.source}
                    </span>
                    <h3 className="mb-1 line-clamp-2 text-sm font-semibold text-foreground-900 transition-colors group-hover:text-primary-700 md:text-base">
                      {item.title}
                    </h3>
                    <p className="line-clamp-2 text-xs leading-relaxed text-foreground-500 md:text-sm">{item.summary}</p>
                  </span>
                </Link>
              ))}
            </div>
          </div>

          <div>
            <SectionHeading eyebrow="Reviews" title="热门影评精选" moreTo="/category/review" />
            <div className="flex flex-col gap-4">
              {hotReviews.map((review) => (
                <Link
                  key={review.id}
                  to={`/detail/review/${review.id}`}
                  className="group rounded-lg border border-background-200 bg-background-50 p-4 transition-all duration-300 hover:-translate-y-1 hover:border-accent-200"
                >
                  <div className="mb-2 flex items-center gap-2">
                    <img
                      src={review.avatar}
                      alt={review.author}
                      className="h-7 w-7 rounded-full object-cover object-top"
                    />
                    <span className="text-xs font-medium text-foreground-700">{review.author}</span>
                    <span className="ml-auto text-xs text-foreground-400">{review.date}</span>
                  </div>
                  <h3 className="mb-1.5 line-clamp-2 text-sm font-semibold text-foreground-900 transition-colors group-hover:text-accent-600">
                    {review.title}
                  </h3>
                  <p className="line-clamp-2 text-xs leading-relaxed text-foreground-500">{review.excerpt}</p>
                  <div className="mt-3 flex items-center gap-2">
                    <Badge tone="secondary" className="text-[10px]">{review.workTitle}</Badge>
                    <span className="flex items-center gap-1 text-xs text-foreground-400">
                      <i className="ri-thumb-up-line" aria-hidden="true"></i>
                      {review.likes}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 当季热映推荐 */}
      <section className="mx-auto w-full max-w-site px-4 py-12 md:px-6 md:py-16">
        <SectionHeading eyebrow="Now Showing" title="当季热映作品推荐" moreTo="/category/movie" moreLabel="浏览更多" />
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {hotWorks.map((work) => (
            <WorkCard key={work.id} work={work} showFavorite />
          ))}
        </div>
      </section>

      {/* 网站简介 CTA */}
      <section className="bg-primary-900">
        <div className="mx-auto flex w-full max-w-site flex-col items-start gap-6 px-4 py-12 md:flex-row md:items-center md:justify-between md:px-6 md:py-14">
          <div className="max-w-2xl">
            <h2 className="font-heading text-xl font-bold text-background-50 md:text-2xl">
              关于亚洲天堂
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-primary-200">
              我们专注于为华语影迷提供及时、专业、有温度的影视内容。无论是院线新片、热播剧集，
              还是深度影评与幕后故事，你都能在这里找到值得停留的理由。
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/about"
              className="rounded-md bg-background-50 px-5 py-2.5 text-sm font-medium text-primary-800 transition-colors hover:bg-primary-100 whitespace-nowrap"
            >
              了解我们
            </Link>
            <Link
              to="/contact"
              className="rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 whitespace-nowrap"
            >
              联系我们
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}